/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
   FaCheck,
   FaTimes,
   FaStar,
   FaVideo,
   FaMapMarkerAlt,
   FaExternalLinkAlt,
} from 'react-icons/fa';

import { SmallButton } from '../../../../../styles/ui/Button';
import StatusBadge from '../../components/StatusBadge';
import InterviewResultBadge from '../../components/InterviewResultBadge';
import {
   STAGE,
   MODALITY,
   MODALITY_LABEL,
   structuredNotesFieldsForRound,
} from '../../constants';
import { formatDateTimeBr } from '../../utils/format';
import {
   Tl,
   TlStep,
   TlNode,
   TlX,
   TlCard,
   TlTitle,
   TlMeta,
   TlEmpty,
   TlHistory,
   TlActions,
   TlLegend,
   NotesBlock,
   InlineHint,
} from '../styles';

// ---------------------------------------------------------------------------
// Esteira em timeline vertical POR RODADA (redesign Claude Design 2026-07-14):
// nós Entrevista 1 → Entrevista 2 → Aprovação final, com estados ok (verde) /
// wait (neutro) / reproved (vermelho + ✕ bloqueando o conector). O card da
// rodada mostra a entrevista DECISIVA (a ativa ou a que tem resultado);
// remarcações anteriores ficam num expand compacto. Roteiro estruturado e
// ações da ativa preservados. A regra de "ativa" (última da rodada com
// result=null) não muda — só a apresentação.
// ---------------------------------------------------------------------------

// Resultados que ENCERRAM o processo naquela rodada (nó vermelho + bloqueio).
const REPROVED_RESULTS_BY_ROUND = {
   1: ['INCOMPATIVEL', 'RECUSA_DE_VAGA', 'NAO_COMPARECEU'],
   2: ['REPROVADO', 'DESISTIU', 'NAO_COMPARECEU'],
};
const APPROVED_RESULT_BY_ROUND = { 1: 'PROXIMA_ETAPA', 2: 'APROVADO' };

function attendedLabel(attended) {
   if (attended === true) return 'Compareceu';
   if (attended === false) return 'Não compareceu';
   return null;
}

// Divide as entrevistas da rodada em { decisive, history }: decisiva = última
// não-REMARCADO (ativa ou concluída); as REMARCADO viram histórico.
function splitRound(interviews, round) {
   const ofRound = interviews.filter((i) => i.round === round);
   const history = ofRound.filter((i) => i.result === 'REMARCADO');
   const decisive =
      [...ofRound].reverse().find((i) => i.result !== 'REMARCADO') ?? null;
   return { decisive, history };
}

function roundState(decisive, round) {
   if (!decisive?.result) return 'wait';
   if (decisive.result === APPROVED_RESULT_BY_ROUND[round]) return 'ok';
   if ((REPROVED_RESULTS_BY_ROUND[round] ?? []).includes(decisive.result)) {
      return 'reproved';
   }
   return 'wait';
}

function StructuredNotes({ interview }) {
   const notes = interview.structured_notes;
   const fields = structuredNotesFieldsForRound(interview.round);
   const rows = [];

   if (notes && typeof notes === 'object') {
      fields.forEach((field) => {
         const value = notes[field.key];
         if (value) rows.push({ label: field.label, value });
      });
   }
   if (interview.notes) {
      rows.push({ label: 'Observações gerais', value: interview.notes });
   }

   if (!rows.length) return null;

   return (
      <NotesBlock>
         {rows.map((row) => (
            <div key={row.label}>
               <div className="note-label">{row.label}</div>
               <div className="note-value">{row.value}</div>
            </div>
         ))}
      </NotesBlock>
   );
}

StructuredNotes.propTypes = {
   interview: PropTypes.shape({
      round: PropTypes.number,
      structured_notes: PropTypes.object,
      notes: PropTypes.string,
   }).isRequired,
};

export default function InterviewTimeline({
   interviews = [],
   activeInterviewId = null,
   interviewerNameById = {},
   showActions = false,
   canManage = false,
   candidateStage = null,
   onResult,
   onReschedule,
   onEdit,
   onOpenOnboarding,
}) {
   // Rodadas com histórico expandido (Set de rounds).
   const [expandedHistory, setExpandedHistory] = useState(() => new Set());

   const toggleHistory = (round) => {
      setExpandedHistory((prev) => {
         const next = new Set(prev);
         if (next.has(round)) next.delete(round);
         else next.add(round);
         return next;
      });
   };

   const interviewerNameOf = (interview) =>
      interview?.interviewer?.name ??
      interviewerNameById[String(interview?.interviewer_user_id)] ??
      null;

   const rounds = [1, 2].map((round) => {
      const { decisive, history } = splitRound(interviews, round);
      return { round, decisive, history, state: roundState(decisive, round) };
   });

   const anyReproved = rounds.some((r) => r.state === 'reproved');
   const isApproved =
      candidateStage === STAGE.ONBOARDING || candidateStage === STAGE.ADMITIDO;
   const isClosed = candidateStage === STAGE.ENCERRADO;

   // Estado/badge do nó final de aprovação.
   const finalState = isApproved ? 'ok' : 'wait';
   let finalBadge = <StatusBadge variant="neutral">Aguardando</StatusBadge>;
   if (isApproved) {
      finalBadge = <StatusBadge variant="green">Aprovado</StatusBadge>;
   } else if (isClosed || anyReproved) {
      finalBadge = <StatusBadge variant="red">Encerrado</StatusBadge>;
   }

   const renderRoundNode = ({ round, state }) => {
      if (state === 'ok') return <FaCheck size={15} />;
      if (state === 'reproved') return <FaTimes size={15} />;
      return String(round);
   };

   const renderRoundCard = ({ round, decisive, history, state }) => {
      const isActive =
         decisive && !decisive.result && decisive.id === activeInterviewId;
      const attended = decisive ? attendedLabel(decisive.attended) : null;
      const interviewerName = decisive ? interviewerNameOf(decisive) : null;
      const modality = decisive?.modality;
      const historyOpen = expandedHistory.has(round);

      return (
         <TlCard $state={state}>
            <TlTitle>
               <h3>Entrevista {round}</h3>
               {modality && (
                  <span className="tl-mode">
                     {modality === MODALITY.PRESENCIAL ? (
                        <FaMapMarkerAlt size={12} />
                     ) : (
                        <FaVideo size={12} />
                     )}
                     {MODALITY_LABEL[modality] ?? modality}
                  </span>
               )}
               <span className="tl-pill">
                  {decisive ? (
                     <InterviewResultBadge value={decisive.result} />
                  ) : (
                     <StatusBadge variant="neutral">Não agendada</StatusBadge>
                  )}
               </span>
            </TlTitle>

            {decisive ? (
               <TlMeta>
                  <span>
                     Agendada:{' '}
                     <strong>{formatDateTimeBr(decisive.scheduled_at)}</strong>
                  </span>
                  {interviewerName && (
                     <span>
                        Entrevistador: <strong>{interviewerName}</strong>
                     </span>
                  )}
                  {attended && (
                     <span>
                        Presença: <strong>{attended}</strong>
                     </span>
                  )}
               </TlMeta>
            ) : (
               <TlEmpty>Aguardando agendamento.</TlEmpty>
            )}

            {decisive && <StructuredNotes interview={decisive} />}

            {history.length > 0 && (
               <TlHistory>
                  <div>
                     <SmallButton
                        type="button"
                        onClick={() => toggleHistory(round)}
                     >
                        {historyOpen
                           ? 'Ocultar remarcações'
                           : `Remarcações (${history.length})`}
                     </SmallButton>
                  </div>
                  {historyOpen &&
                     history.map((entry) => (
                        <span key={entry.id}>
                           Remarcada —{' '}
                           {MODALITY_LABEL[entry.modality] ?? entry.modality} ·{' '}
                           {formatDateTimeBr(entry.scheduled_at)}
                           {interviewerNameOf(entry)
                              ? ` · ${interviewerNameOf(entry)}`
                              : ''}
                        </span>
                     ))}
               </TlHistory>
            )}

            {showActions && canManage && isActive && (
               <TlActions>
                  <SmallButton
                     type="button"
                     $intent="cta"
                     onClick={() => onResult(decisive)}
                  >
                     Registrar resultado
                  </SmallButton>
                  <SmallButton
                     type="button"
                     onClick={() => onReschedule(decisive)}
                  >
                     Remarcar
                  </SmallButton>
                  <SmallButton type="button" onClick={() => onEdit(decisive)}>
                     Editar
                  </SmallButton>
               </TlActions>
            )}
         </TlCard>
      );
   };

   const hasAnything =
      interviews.length > 0 || candidateStage !== STAGE.BANCO_CURRICULOS;

   if (!hasAnything) {
      return <InlineHint>Nenhuma entrevista registrada ainda.</InlineHint>;
   }

   return (
      <>
         <Tl>
            {rounds.map((roundInfo) => (
               <TlStep key={roundInfo.round} $state={roundInfo.state}>
                  <TlNode $state={roundInfo.state}>
                     {renderRoundNode(roundInfo)}
                  </TlNode>
                  {roundInfo.state === 'reproved' && (
                     <TlX>
                        <FaTimes size={9} />
                     </TlX>
                  )}
                  {renderRoundCard(roundInfo)}
               </TlStep>
            ))}

            {/* ─── Aprovação final ───────────────────────────────────── */}
            <TlStep $state={finalState}>
               <TlNode $state={finalState}>
                  <FaStar size={14} />
               </TlNode>
               <TlCard $state={finalState}>
                  <TlTitle>
                     <h3>Aprovação final</h3>
                     <span className="tl-pill">{finalBadge}</span>
                  </TlTitle>
                  <div style={{ marginTop: 10 }}>
                     <SmallButton
                        type="button"
                        $intent="cta"
                        disabled={!isApproved}
                        onClick={isApproved ? onOpenOnboarding : undefined}
                     >
                        <FaExternalLinkAlt size={11} /> Abrir onboarding
                     </SmallButton>
                     {!isApproved && (
                        <TlEmpty style={{ marginTop: 8 }}>
                           {isClosed || anyReproved
                              ? 'Processo encerrado nas entrevistas.'
                              : 'Disponível após aprovação nas entrevistas.'}
                        </TlEmpty>
                     )}
                  </div>
               </TlCard>
            </TlStep>
         </Tl>

         <TlLegend>
            <span className="lg">
               <span className="swatch ok" /> Concluída / aprovada
            </span>
            <span className="lg">
               <span className="swatch wait" /> Pendente
            </span>
            <span className="lg">
               <span className="swatch bad" /> Reprovado — bloqueia a etapa
               seguinte
            </span>
         </TlLegend>
      </>
   );
}

InterviewTimeline.propTypes = {
   interviews: PropTypes.arrayOf(PropTypes.object),
   activeInterviewId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
   ]),
   interviewerNameById: PropTypes.objectOf(PropTypes.string),
   showActions: PropTypes.bool,
   canManage: PropTypes.bool,
   candidateStage: PropTypes.string,
   onResult: PropTypes.func,
   onReschedule: PropTypes.func,
   onEdit: PropTypes.func,
   onOpenOnboarding: PropTypes.func,
};
